<template>
  <v-app>
    <AppHeader
      :banner-image="bannerImage"
      :banner-text-color="bannerTextColor"
      :brand-logo="brandLogo"
      :brand-url="brandURL"
      v-model:user-file="userFile"
      @import-settings="import_settings"
      @restore-defaults="restore_defaults"
      @generate="generate"
      @toggle-dark="toggleDark"
      @drop-file="addDropFile"
    />

    <v-main>
      <v-container id="config-section">
        <v-spacer></v-spacer>

        <v-row align="center" class="mb-4">
          <v-col cols="12" md="3" lg="2">
            <ModuleSelector
              v-model:current-module="currentModule"
              :module-names="Object.keys(modules)"
            />
          </v-col>
          <v-col
            cols="4"
            md="8"
            lg="10"
            v-if="extraButtons && extraButtons.length"
          >
            <div class="d-flex flex-wrap justify-end">
              <v-btn
                v-for="(btn, idx) in extraButtons"
                :key="idx"
                :href="btn.url"
                target="_blank"
                rel="noopener"
                :color="btn.color || 'primary'"
                variant="elevated"
                class="ma-1"
        :prepend-icon="btn.icon || undefined"
              >
                {{ btn.label || 'Link' }}
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col v-for="(option, index) in options" :key="option.name" cols="4">
            <OptionCard
              :option="option"
              @update:option="updateOption(index, $event)"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-btn variant="elevated" color="primary" @click="generate" width="100%">
              Download File
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>
<script>
import optionsData from "./module_options.json"
import { useTheme } from 'vuetify'
import AppHeader from './AppHeader.vue'
import ModuleSelector from './ModuleSelector.vue'
import OptionCard from './OptionCard.vue'

export default {
  name: 'ModuleConfigurator',
  components: {
    AppHeader,
    ModuleSelector,
    OptionCard
  },
  props: {},
  setup() {
    const theme = useTheme()
    return { theme }
  },
  data() {
    return {
      options: optionsData.BloomV2.options,
      modules: optionsData,
      userFile: null,
      fileUploadErr: null,
      currentModule: "BloomV2",
      bannerImage: optionsData.BloomV2.module_data.banner_image,
      bannerTextColor: optionsData.BloomV2.module_data.banner_text_color || null,
      brandLogo: "/Qu-Bit_Text_Logo.svg",
      brandURL: "https://qubitelectronix.com"
    }
  },
  watch: {
    currentModule() {
      this.updateOptions()
    }
  },
  computed: {
    extraButtons() {
      const md = this.modules?.[this.currentModule]?.module_data || {}
      return Array.isArray(md.extra_buttons) ? md.extra_buttons : []
    }
  },
  mounted() {
    // component is now mounted
    // read in the json
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      this.theme.global.name.value = 'dark'
    }

  },
  methods: {
    updateOption(index, updatedOption) {
      this.options[index] = updatedOption
    },
    generate() {
      let testdata = this.options
      let module_name = optionsData[this.currentModule].module_data.firmware_id
      // real quick make a mini version
      let outdata = {}
      outdata[module_name] = []
      testdata.forEach(item => outdata[module_name].push({ "name": item.name, "value": item.value }))
      let filename = "options.json";
      let text = JSON.stringify(outdata, null, 2);
      let element = document.createElement('a');
      element.setAttribute('href', 'data:application/json;charset=utf-8,' + encodeURIComponent(text));
      element.setAttribute('download', filename);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    },
    restore_defaults() {
      this.options.forEach(item => item.value = item.default)
    },
    import_settings() {
      this.fileUploadErr = null;
      if (!this.userFile) return;

      const reader = new FileReader();

      if (this.userFile.name.includes(".json")) {
        reader.onload = (res) => {
          try {
            // now we actually want to do the stuff
            const obj = JSON.parse(res.target.result);
            const topLevelKeys = Object.keys(obj);
            if (!topLevelKeys.length) {
              return this.notifyError('JSON file is empty or malformed');
            }

            // Find a module whose firmware_id matches one of the top-level keys
            let matchedModuleName = null;
            let matchedFirmwareId = null;
            for (const moduleName of Object.keys(this.modules)) {
              const fwId = this.modules[moduleName]?.module_data?.firmware_id;
              if (fwId && topLevelKeys.includes(fwId)) {
                matchedModuleName = moduleName;
                matchedFirmwareId = fwId;
                break;
              }
            }

            if (!matchedModuleName) {
              return this.notifyError('No compatible module found in JSON file');
            }

            const importedOptions = obj[matchedFirmwareId];
            if (!Array.isArray(importedOptions)) {
              return this.notifyError('Invalid options format in JSON (expected array)');
            }

            const applyImportedOptions = () => {
              // Create a quick lookup map for local options by name
              const localOptionMap = new Map(this.options.map(o => [o.name, o]));
              importedOptions.forEach(opt => {
                if (!opt || typeof opt.name !== 'string' || !('value' in opt)) return;
                const local = localOptionMap.get(opt.name);
                if (local) {
                  // Basic assignment (could add validation here later)
                  local.value = opt.value;
                }
              });
            };

            if (matchedModuleName !== this.currentModule) {
              // Switch module first, then apply after options reactive ref updates
              this.currentModule = matchedModuleName;
              this.$nextTick(() => {
                applyImportedOptions();
              });
            } else {
              applyImportedOptions();
            }
            } catch (error) {
            console.error('Error parsing JSON file:', error);
            this.notifyError('Invalid JSON file');
          }
        };
        reader.onerror = (err) => {
          console.error('Error reading file:', err);
          this.notifyError('Error reading file');
        };
        reader.readAsText(this.userFile);
      } else {
        this.notifyError("Unsupported file type (expected .json)");
        reader.onload = (res) => {
          console.log(res.target.result);
        };
        reader.onerror = (err) => {
          console.error('Error reading file:', err);
        };
        reader.readAsText(this.userFile);
      }
    },
    addDropFile(e) {
      this.userFile = e.dataTransfer.files[0]
      this.import_settings()
    },
    toggleDark() {
      this.theme.global.name.value = this.theme.global.current.value.dark ? 'light' : 'dark'
    },
    updateOptions() {
      this.options = this.modules[this.currentModule].options
      this.bannerImage = this.modules[this.currentModule].module_data.banner_image
      this.bannerTextColor = this.modules[this.currentModule].module_data.banner_text_color || null
    },
    notifyError(msg) {
      this.fileUploadErr = msg
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(msg)
      }
    }
  }
}
</script>

<style>
.ocean {
  background: url('./img/ocean_big_square.png') no-repeat center center fixed !important;
  background-size: cover;
}

/* Add smooth transition for theme changes */
.v-application {
  transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
}

/* Apply transition to main content areas */
.v-main {
  transition: background-color 0.3s ease-in-out;
}
</style>